import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Atom, ArrowsClockwise, CheckCircle, WarningCircle, HourglassMedium, Sparkle, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatCurrency } from "@/pages/sheetUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  synced: { label: "Sincronizado", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle },
  pending: { label: "Pendente", cls: "bg-amber-100 text-amber-700 animate-pulse", Icon: HourglassMedium },
  processing: { label: "Processando", cls: "bg-sky-100 text-sky-700 animate-pulse", Icon: HourglassMedium },
  rejected: { label: "Rejeitado", cls: "bg-rose-100 text-rose-700", Icon: WarningCircle },
  error: { label: "Erro", cls: "bg-rose-100 text-rose-700", Icon: WarningCircle },
};

export default function MolecularIntelligencePanel({ user, refreshSheets }) {
  const [drafts, setDrafts] = useState([]);
  const [mirror, setMirror] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const isViewer = user?.role === "viewer";

  const fetchDrafts = useCallback(async () => {
    try {
      const [{ data }, ms] = await Promise.all([
        axios.get(`${API}/atlas-core/drafts`),
        axios.get(`${API}/atlas-core/mirror-status`).catch(() => null),
      ]);
      setDrafts(data.drafts || []);
      if (ms) setMirror(ms.data);
    } catch (e) {
      // silencioso no polling
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
    const interval = setInterval(fetchDrafts, 10000);
    return () => clearInterval(interval);
  }, [fetchDrafts]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/atlas-core/sync-now`);
      toast.success(`Sincronização: ${data.synced} materializada(s), ${data.rejected} rejeitada(s)`);
      await fetchDrafts();
      refreshSheets?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const handlePromote = async (draft) => {
    try {
      await axios.post(`${API}/atlas-core/drafts/${draft.sop_raft_sheet_id}/promote`);
      toast.success(`"${draft.title}" promovida a ficha oficial`);
      await fetchDrafts();
      refreshSheets?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao promover rascunho");
    }
  };

  const handleRetryMirror = async () => {
    try {
      const { data } = await axios.post(`${API}/atlas-core/mirror-retry`);
      toast.success(`Reprocessamento: ${data.reset_to_pending} evento(s) reenfileirado(s)`);
      await fetchDrafts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reprocessar espelhamento");
    }
  };

  return (
    <div data-testid="atlas-drafts-panel">
      {mirror && (
        <div className="flex items-center gap-2 flex-wrap mb-3 px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-100 text-[11px] text-zinc-600" data-testid="mirror-status-strip">
          <span className="font-bold uppercase tracking-wider text-zinc-500">Espelhamento → Atlas Core</span>
          <span className={`px-1.5 py-0.5 rounded font-bold ${mirror.enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`}>
            {mirror.enabled ? "Ativo" : "Aguardando ativação"}
          </span>
          <span>{mirror.counts?.pending || 0} pendente(s)</span>
          <span className="text-emerald-700">{mirror.counts?.sent || 0} enviado(s)</span>
          {(mirror.counts?.failed || 0) > 0 && (
            <>
              <span className="text-rose-600 font-bold">{mirror.counts.failed} falha(s)</span>
              {!isViewer && (
                <button onClick={handleRetryMirror} className="underline text-rose-700 hover:text-rose-900" data-testid="mirror-retry-btn">
                  Reprocessar
                </button>
              )}
            </>
          )}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          Sincronização contínua com o FAWKES ATLAS CORE ativa — sem exportação manual
        </div>
        {!isViewer && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleSyncNow} disabled={syncing} data-testid="atlas-sync-now-btn">
            <ArrowsClockwise size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            Sincronizar agora
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-zinc-400 py-8 text-center">Carregando harmonizações...</p>
      ) : drafts.length === 0 ? (
        <div className="text-center py-12">
          <Atom size={48} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500">Nenhuma harmonização recebida do Atlas Core ainda</p>
          <p className="text-sm text-zinc-400 mt-1">Crie uma harmonização no Canvas ou via SousChef do Atlas Core — ela aparece aqui automaticamente</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((d) => {
            const st = STATUS_CONFIG[d.sync_status] || STATUS_CONFIG.pending;
            return (
              <div key={d.atlas_id} className="border border-zinc-200 rounded-lg p-4" data-testid={`atlas-draft-card-${d.atlas_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Sparkle size={16} className="text-amber-500 shrink-0" />
                      <p className="text-sm font-semibold text-zinc-900">{d.title}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1 ${st.cls}`} data-testid={`atlas-status-${d.atlas_id}`}>
                        <st.Icon size={11} /> {st.label}
                      </span>
                      {d.sheet_status === "active" && (
                        <span className="text-[10px] bg-zinc-900 text-white px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1">
                          <SealCheck size={11} /> Oficial
                        </span>
                      )}
                    </div>
                    {d.harmonization_description && (
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{d.harmonization_description}</p>
                    )}
                    {d.reject_reason && (
                      <p className="text-xs text-rose-600 mt-1">{d.reject_reason}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {d.log_jaccard_index != null && (
                        <span className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded font-mono font-bold">
                          Jaccard log: {Number(d.log_jaccard_index).toFixed(4)}
                        </span>
                      )}
                      {(d.dominant_volatile_compounds || []).map((c) => (
                        <span key={c} className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {d.total_cost != null && (
                      <>
                        <p className="text-[10px] uppercase tracking-wider text-zinc-400">CMV Calculado</p>
                        <p className="text-sm font-mono font-bold text-zinc-900">{formatCurrency(d.total_cost)}</p>
                        <p className="text-[11px] font-mono text-emerald-700">{formatCurrency(d.cost_per_unit)}/un</p>
                      </>
                    )}
                    {!isViewer && d.sync_status === "synced" && d.sheet_status === "draft" && (
                      <Button size="sm" className="h-7 mt-2 text-xs bg-zinc-950 text-white hover:bg-zinc-800"
                        onClick={() => handlePromote(d)} data-testid={`promote-draft-${d.sop_raft_sheet_id}`}>
                        <SealCheck size={13} className="mr-1" /> Promover a oficial
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-zinc-100 text-[11px] text-zinc-400 flex-wrap">
                  {d.operator_email && <span>Operador: {d.operator_email}</span>}
                  <span>{d.ingredients_count} insumo(s)</span>
                  {d.created_ingredients?.length > 0 && <span>{d.created_ingredients.length} insumo(s) auto-criado(s)</span>}
                  {d.synced_at && <span className="ml-auto">Sincronizado: {new Date(d.synced_at).toLocaleString("pt-BR")}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
